import { ShieldCheck, Wrench, CheckCircle, Circle, FilePdf, FolderOpen } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import AuthImage from "../AuthImage";
import { openAuthFile, downloadAuthFile } from "../../lib/fileOpen";
import { Section } from "./PortalPage";
import { fmtDateTime } from "./portalUtils";
import { Button } from "../ui/button";

const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "");

function Steps({ steps, testid }) {
  return (
    <ul className="space-y-2">
      {steps.map((s, i) => (
        <li key={i} className="flex items-start gap-2 text-xs" data-testid={`${testid}-step-${i}`} data-done={s.done}>
          {s.done ? <CheckCircle size={16} className="shrink-0 text-emerald-600 mt-0.5" weight="fill" /> : <Circle size={16} className="shrink-0 text-muted-foreground mt-0.5" />}
          <span className={`flex-1 ${s.done ? "" : "text-muted-foreground"}`}>{s.text}{s.done && <span className="text-muted-foreground"> · {hhmm(s.done_at)}</span>}</span>
          {s.photo && (
            <button type="button" onClick={() => openAuthFile(s.photo.url)} className="shrink-0 h-12 w-12 rounded-md border border-border overflow-hidden bg-muted" title="Open photo" data-testid={`${testid}-step-${i}-photo`}>
              <AuthImage path={s.photo.url} alt="Step photo" className="h-full w-full object-cover" />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

/* Client portal: "Made safe at 10:42" with each make-safe step, its time and the engineer's evidence photo, the PDF report,
   then the follow-up repair steps as they are confirmed. */
export function MadeSafePanel({ madeSafe, jobId, reference, testid = "emergency-made-safe" }) {
  if (!madeSafe) return null;
  const repair = madeSafe.repair || { steps: [], done: 0, total: 0 };
  if (!madeSafe.done && !repair.done) return null;
  const allSafe = madeSafe.total > 0 && madeSafe.done === madeSafe.total;
  const allFixed = repair.total > 0 && repair.done === repair.total;
  return (
    <Section className="mb-6" title={<span className="flex items-center gap-1.5"><ShieldCheck size={14} weight="fill" /> {allSafe ? "Made safe" : "Making safe"}</span>} testid={testid}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className={`text-sm font-semibold ${allSafe ? "text-emerald-700 dark:text-emerald-400" : ""}`} data-testid={`${testid}-status`}>
            {allSafe ? `Made safe at ${hhmm(madeSafe.made_safe_at)}` : `${madeSafe.done} of ${madeSafe.total} make-safe steps confirmed so far`}
          </p>
          <p className="text-[11px] text-muted-foreground mb-3">{allSafe ? `Confirmed on site ${fmtDateTime(madeSafe.made_safe_at)} with a photo for every step.` : "Each step is confirmed on site with a photo as it's done."}</p>
        </div>
        {madeSafe.report_available && jobId && (
          <div className="flex flex-col items-end gap-1">
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => downloadAuthFile(`/api/client-hub/emergencies/${jobId}/made-safe.pdf`, `${reference || "emergency"}_Made-Safe-Report.pdf`)} data-testid={`${testid}-pdf-btn`}>
              <FilePdf size={14} className="mr-1" /> Download Made Safe report (PDF)
            </Button>
            {madeSafe.report_file && (
              <Link to={`/portal/files/${madeSafe.report_file.folder_id}`} className="text-[11px] text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid={`${testid}-hub-link`}>
                <FolderOpen size={12} /> Filed in your File Hub
              </Link>
            )}
          </div>
        )}
      </div>
      <Steps steps={madeSafe.steps} testid={testid} />
      {repair.total > 0 && (
        <div className="mt-4 pt-4 border-t border-border" data-testid={`${testid}-repair`} data-done={repair.done} data-total={repair.total}>
          <p className={`text-sm font-semibold flex items-center gap-1.5 ${allFixed ? "text-emerald-700 dark:text-emerald-400" : ""}`} data-testid={`${testid}-repair-status`}>
            <Wrench size={14} weight="fill" /> {allFixed ? `Repair complete at ${hhmm(repair.completed_at)}` : `Repair in progress — ${repair.done} of ${repair.total} steps done`}
          </p>
          <p className="text-[11px] text-muted-foreground mb-3">{allFixed ? "Every follow-up repair step was confirmed on site with a photo." : "The engineer confirms each repair step with a photo as the fix progresses."}</p>
          <Steps steps={repair.steps} testid={`${testid}-repair`} />
        </div>
      )}
    </Section>
  );
}
